import {createContext, type PropsWithChildren, use, useCallback, useMemo} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {dataRetryPolicy, fetchBackend} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import {UserProfile} from "@/lib/types";
import {useGlobalSearchParams} from "expo-router";

const ProfileContext = createContext<{
    profile?: UserProfile;
    profileLoading: boolean;
    profileError: Error | null;
    refreshProfile: () => any;
}>({
    profile: undefined,
    profileLoading: true,
    profileError: null,
    refreshProfile: () => {
    },
});

export function useProfile() {
    const value = use(ProfileContext);
    if (!value) {
        throw new Error('useProfile must be wrapped in a <ProfileProvider />');
    }
    return value;
}

export function ProfileProvider({children}: PropsWithChildren) {
    const queryClient = useQueryClient();
    const {session, ...sessionParams} = useSession();

    const {username} = useGlobalSearchParams();
    const queryKey = useMemo(() => ['profile', username, session], [username, session]);

    const refreshProfile = useCallback(async () => {
        await queryClient.invalidateQueries({queryKey});
    }, [queryClient, queryKey]);

    const {data, isLoading, error} = useQuery<UserProfile>({
        queryKey,
        queryFn: () => fetchBackend(`/profile/${username}`, "GET", session),
        staleTime: 1000 * 60 * 15,
        retry: dataRetryPolicy,
        enabled: !!username && !sessionParams.isLoading,
    })

    return (
        <ProfileContext.Provider
            value={{
                profile: data,
                refreshProfile,
                profileLoading: isLoading,
                profileError: error,
            }}>
            {children}
        </ProfileContext.Provider>
    );
}